import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithOptionalAuth } from './baseQuery';

export interface SearchPayload {
  searchText: string;
  subject: string;
  minPrice: number;
  maxPrice: number;
  sessionType: string;
  grade?: string;  // Grade ID as string
  startDate?: string;  // Format: YYYY-MM-DD
  endDate?: string;    // Format: YYYY-MM-DD
}

export interface SearchResult {
  classId: string;
  courseId: number;
  name: string;
  desc: string;
  fees: number;
  grade: string;
  tutorId: number;
  tutorName: string;
  subject: string;
  imagePath: string | null;
  location?: 'online' | 'in person';
}

export interface SearchResponse {
  statusCode: number;
  data: {
    count: number;
    arrayObj: SearchResult[];
  };
}

export const searchApi = createApi({
  reducerPath: 'searchApi',
  baseQuery: baseQueryWithOptionalAuth, // Uses auth if available but doesn't require it
  endpoints: (builder) => ({
    searchCourses: builder.mutation<SearchResponse, SearchPayload>({
      query: (payload) => {
        // Filter out empty, null, or undefined values
        const cleanPayload = Object.entries(payload).reduce<Partial<SearchPayload>>((acc, [key, value]) => {
          // Special handling for different fields
          if (key === 'minPrice') {
            // For minPrice, include if it's a number (including 0 for free classes)
            if (typeof value === 'number' && value >= 0) {
              (acc as any)[key] = value;
            }
          } else if (key === 'maxPrice') {
            // For maxPrice, exclude if it's 0, 1000 (default), or negative
            if (typeof value === 'number' && value > 0 && value !== 1000) {
              (acc as any)[key] = value;
            }
          } else if (key === 'subject') {
            // For subject, check if it's a non-empty string after trimming
            if (typeof value === 'string' && value.trim() !== '') {
              (acc as any)[key] = value;
            }
          } else if (key === 'grade') {
            // For grade, check if it's a non-empty string (grade ID)
            if (typeof value === 'string' && value.trim() !== '' && value !== '0') {
              (acc as any)[key] = value;
            }
          } else if (key === 'searchText' && value.trim() !== '') {
            // For searchText, include even if empty (to get all results)
            if (typeof value === 'string') {
              (acc as any)[key] = value;
            }
          } else if (value !== null && value !== undefined && value !== '') {
            // For other fields (sessionType, dates), exclude null, undefined, or empty strings
            (acc as any)[key] = value;
          }
          return acc;
        }, {} as any);
        
        return {
          url: '/search/search',
          method: 'POST',
          body: cleanPayload,
        };
      },
    }),
  }),
});

export const { useSearchCoursesMutation } = searchApi;
